'use client';

import { Box, LinearProgress, useMediaQuery } from '@mui/material';

import { FADE_MS, TICK_MS, useNavigationProgress } from '@/hooks/useNavigationProgress';

const BAR_HEIGHT = 3;

/**
 * The trailing "comet" glow nextjs-toploader draws at the leading edge. Purely decorative, so it
 * is dropped entirely under prefers-reduced-motion (the bar's fill itself stays — that is
 * information, not decoration).
 */
const cometSx = glow => ({
	content: '""',
	position: 'absolute',
	right: 0,
	top: 0,
	bottom: 0,
	width: 100,
	transform: 'rotate(3deg) translateY(-4px)',
	boxShadow: `0 0 10px ${glow}, 0 0 5px ${glow}`
});

const buildSx = (theme, { finishing, reduceMotion }) => {
	const glow = theme.palette.lumoraGreenPrimary.main;
	// MUI's determinate bar defaults to `transform .4s linear`; matching the ramp interval makes
	// each discrete step land as the next fires, so it reads as one continuous glide.
	const barSx = { transition: reduceMotion ? 'none' : `transform ${TICK_MS}ms linear` };
	if (!reduceMotion) barSx['&::after'] = cometSx(glow);

	return {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		// Above the LumoraWrapper AppBar (1100) / Drawer (1200), dialogs (1300) and snackbars (1400).
		zIndex: theme.zIndex.tooltip + 100,
		pointerEvents: 'none',
		opacity: finishing ? 0 : 1,
		transition: reduceMotion ? 'none' : `opacity ${FADE_MS}ms ease-out`,
		'& .MuiLinearProgress-root': {
			height: BAR_HEIGHT,
			// No track: the toploader look is a lone bar over the page, not a filled rail.
			backgroundColor: 'transparent'
		},
		'& .MuiLinearProgress-bar': barSx
	};
};

/**
 * Global navigation progress bar, in the style of nextjs-toploader.
 *
 * Mounted once in the root layout alongside GlobalSnackbar. Renders nothing until a navigation has
 * been pending long enough to be worth showing, so prefetched routes never flash a bar.
 *
 * @example
 * // src/app/layout.jsx
 * <ClientThemeProvider>
 *     <GlobalTopLoader />
 *     {children}
 * </ClientThemeProvider>
 */
const GlobalTopLoader = () => {
	const { value, visible, finishing } = useNavigationProgress();
	const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });

	if (!visible) return null;

	return (
		<Box data-testid="global-top-loader" sx={theme => buildSx(theme, { finishing, reduceMotion })}>
			<LinearProgress
				variant="determinate"
				value={value}
				color="lumoraGreenPrimary"
				aria-label="Page navigation progress"
			/>
		</Box>
	);
};

export default GlobalTopLoader;
